import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingTranslations } from '@/constants/meeting.translations';
import { useMeetings } from '@/hooks/useMeetings';
import MeetingRoom from '@/components/meetings/MeetingRoom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';

const MeetingRoomPage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = getMeetingTranslations(language);
  
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isJoining, setIsJoining] = useState(true);

  const {
    currentMeeting,
    joinMeeting,
    leaveMeeting,
    endMeeting,
    isLoading,
  } = useMeetings(user?.id || '');

  useEffect(() => {
    if (user) {
      setUserName(user.displayName || user.email || 'Guest');
      // TODO: Get actual user role from context
      setUserRole('participant');
    }
  }, [user]);

  useEffect(() => {
    const join = async () => {
      if (meetingId && user && userName) {
        const success = await joinMeeting(meetingId, userName, userRole || 'participant');
        setIsJoining(!success);
      }
    };
    
    if (isJoining && meetingId && user && userName) {
      join();
    }
  }, [meetingId, user, userName, userRole, joinMeeting, isJoining]);

  const handleLeave = async (audioBlob?: Blob) => {
    if (meetingId) {
      if (audioBlob && audioBlob.size > 0) {
        // End meeting with audio for transcription
        await endMeeting(meetingId, audioBlob);
      } else {
        await leaveMeeting(meetingId);
      }
      navigate(-1);
    }
  };

  if (isLoading || isJoining) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#1D314D]" />
          <p className="text-[#1D314D]">{language === 'fr' ? 'Connexion à la réunion...' : 'Connecting to meeting...'}</p>
        </div>
      </div>
    );
  }

  if (!currentMeeting) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#1D314D] mb-4">{language === 'fr' ? 'Réunion introuvable' : 'Meeting not found'}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Go Back'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1D314D]">{currentMeeting.title}</h1>
            <p className="text-sm text-muted-foreground">
              {t.deal}: {currentMeeting.dealId.substring(0, 8)}...
            </p>
          </div>
        </div>

        {/* Meeting Room */}
        <MeetingRoom
          roomName={currentMeeting.roomName}
          userName={userName}
          userRole={userRole}
          dealTitle={currentMeeting.title}
          recordingEnabled={currentMeeting.recordingEnabled}
          onLeave={handleLeave}
        />
      </div>
    </div>
  );
};

export default MeetingRoomPage;
